// client/src/pages/admin/KratosIdentities.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuItem,
  InputAdornment,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { kratosAdmin } from '../../api/admin';
import { isSuperAdmin, isAdmin } from '../../utils/accessControl';

// ============================================================================
// Форматирование даты
// ============================================================================

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function getIdentityEmail(identity) {
  return identity?.traits?.email || '—';
}

function getIdentityName(identity) {
  return identity?.traits?.name || 'Без имени';
}

function getIdentityId(identity) {
  return identity?.id || '—';
}

// ============================================================================
// Модал создания
// ============================================================================

function CreateIdentityModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.email.trim() || !form.password.trim()) {
      setError('Email и пароль обязательны');
      return;
    }
    if (form.password.length < 8) {
      setError('Пароль должен быть не короче 8 символов');
      return;
    }

    setLoading(true);
    try {
      await kratosAdmin.createIdentity({
        schema_id: 'default',
        traits: {
          email: form.email.trim(),
          name: form.name.trim() || form.email.split('@')[0],
        },
        credentials: {
          password: {
            config: { password: form.password },
          },
        },
        state: 'active',
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Не удалось создать пользователя');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Создать Kratos identity</DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Игрок (players-профиль) создастся автоматически при первом входе в игру.
          </Alert>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={loading}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Пароль (мин. 8 символов)"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            disabled={loading}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Имя (display name)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={loading}
            margin="normal"
            helperText="Необязательно. Если пусто — возьмётся часть email до @"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {loading ? 'Создание…' : 'Создать'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ============================================================================
// Модал редактирования
// ============================================================================

function EditIdentityModal({ identity, onClose, onSuccess }) {
  const [form, setForm] = useState({
    email: identity?.traits?.email || '',
    name: identity?.traits?.name || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.email.trim()) {
      setError('Email обязателен');
      return;
    }

    setLoading(true);
    try {
      await kratosAdmin.updateIdentity(
        identity.id,
        {
          email: form.email.trim(),
          name: form.name.trim() || form.email.split('@')[0],
        },
        identity.state || 'active'
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Не удалось обновить пользователя');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редактировать identity</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            ID: {identity.id}
          </Typography>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={loading}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Имя (display name)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={loading}
            margin="normal"
          />
          <Alert severity="warning" sx={{ mt: 2 }}>
            Изменения в Kratos <strong>не синхронизируются</strong> с `players.email`/`display_name`.
            Только identity.
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          >
            {loading ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ============================================================================
// Модал удаления (только superadmin)
// ============================================================================

function DeleteIdentityModal({ identity, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const expected = (identity?.traits?.email || 'DELETE').toUpperCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirmText.trim().toUpperCase() !== expected) {
      setError(`Введите "${expected}" для подтверждения`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await kratosAdmin.deleteIdentity(identity.id);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Не удалось удалить пользователя');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ Удалить Kratos identity
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Необратимо.</strong> Identity будет удалён из Kratos.
            <br />
            <strong>players-профиль останется</strong> (прогресс, персонажи, роль),
            но потеряет связь с identity. Восстановление через пересоздание identity
            с тем же email <strong>не вернёт</strong> прогресс.
          </Alert>
          <Typography variant="body2">
            Удалить: <strong>{getIdentityEmail(identity)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            ID: {identity.id}
          </Typography>
          <TextField
            fullWidth
            label={`Введите "${expected}" для подтверждения`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={loading}
            margin="normal"
            autoFocus
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Удаление…' : 'Удалить навсегда'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================

export default function KratosIdentities() {
  const auth = useSelector((state) => state.auth);
  const canDelete = isSuperAdmin(auth);
  const canManage = isAdmin(auth);

  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionIdentity, setActionIdentity] = useState(null);
  const [stateLoadingId, setStateLoadingId] = useState(null);

  const loadIdentities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await kratosAdmin.listIdentities(1, 1000);
      setIdentities(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки identities');
      setIdentities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return identities;
    return identities.filter((i) => {
      const email = i?.traits?.email?.toLowerCase() || '';
      const name = i?.traits?.name?.toLowerCase() || '';
      const id = (i?.id || '').toLowerCase();
      return email.includes(term) || name.includes(term) || id.includes(term);
    });
  }, [identities, search]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const stats = useMemo(() => {
    const total = identities.length;
    const active = identities.filter((i) => i.state === 'active').length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [identities]);

  const handleActionMenuOpen = (event, identity) => {
    setActionMenuAnchor(event.currentTarget);
    setActionIdentity(identity);
  };
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionIdentity(null);
  };

  const handleEdit = (identity) => {
    setEditTarget(identity);
    handleActionMenuClose();
  };
  const handleDelete = (identity) => {
    setDeleteTarget(identity);
    handleActionMenuClose();
  };

  const handleToggleState = async (identity) => {
    const newState = identity.state === 'active' ? 'inactive' : 'active';
    setStateLoadingId(identity.id);
    setError(null);
    setSuccess(null);
    try {
      await kratosAdmin.toggleUserState(identity.id, newState);
      setSuccess(
        newState === 'active'
          ? `Пользователь ${getIdentityEmail(identity)} разблокирован`
          : `Пользователь ${getIdentityEmail(identity)} заблокирован`
      );
      await loadIdentities();
    } catch (err) {
      setError(err.message || 'Не удалось изменить состояние');
    } finally {
      setStateLoadingId(null);
      handleActionMenuClose();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Kratos identities
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Управление учётными записями Kratos (не путать с игроками в{' '}
          <a href="/admin/users">/admin/users</a>)
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Всего identities
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Активных
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Заблокированных
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <TextField
            size="small"
            placeholder="Поиск по email / имени / ID"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: { md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadIdentities}
              disabled={loading}
            >
              Обновить
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={!canManage}
            >
              Создать identity
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Состояние</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && identities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Identities не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((identity) => {
                  const email = getIdentityEmail(identity);
                  const name = getIdentityName(identity);
                  const id = getIdentityId(identity);
                  const state = identity.state || 'unknown';
                  const isActive = state === 'active';

                  return (
                    <TableRow key={identity.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {id.substring(0, 8)}…
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? 'Активен' : 'Заблокирован'}
                          color={isActive ? 'success' : 'error'}
                          variant={isActive ? 'filled' : 'outlined'}
                          icon={isActive ? <LockOpenIcon /> : <LockIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={identity.created_at || ''}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="caption">
                              {formatDate(identity.created_at)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionMenuOpen(e, identity)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250, 500]}
          labelRowsPerPage="Строк:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Paper>

      {/* Меню действий */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => handleEdit(actionIdentity)} disabled={!canManage}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Редактировать
        </MenuItem>
        <MenuItem
          onClick={() => handleToggleState(actionIdentity)}
          disabled={!canManage || stateLoadingId === actionIdentity?.id}
        >
          {actionIdentity?.state === 'active' ? (
            <>
              <LockIcon fontSize="small" sx={{ mr: 1 }} />
              Заблокировать
            </>
          ) : (
            <>
              <LockOpenIcon fontSize="small" sx={{ mr: 1 }} />
              Разблокировать
            </>
          )}
        </MenuItem>
        <Divider />
        <Tooltip
          title={canDelete ? '' : 'Только superadmin может удалять identity'}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={() => handleDelete(actionIdentity)}
              disabled={!canDelete}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Удалить навсегда
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {createOpen && (
        <CreateIdentityModal
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setSuccess('Identity создан');
            loadIdentities();
          }}
        />
      )}

      {editTarget && (
        <EditIdentityModal
          identity={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            setSuccess('Identity обновлён');
            loadIdentities();
          }}
        />
      )}

      {deleteTarget && (
        <DeleteIdentityModal
          identity={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => {
            setSuccess(`Identity ${getIdentityEmail(deleteTarget)} удалён`);
            loadIdentities();
          }}
        />
      )}
    </Container>
  );
}